import type { StatusToken } from "@lib/config/statusTokens";
import { cn } from "@lib/utils/cn";
import type { LucideIcon } from "lucide-react";
import type { CSSProperties } from "react";

/**
 * Canonical module status config (plan-business-semantic §2.5.2).
 * One config/statuses.ts per feature satisfies this shape; the wrapper
 * resolves the backend code and passes the module map down.
 */
export interface StatusConfig {
  /** Backend wire format, ex: "EN_ATTENTE" */
  readonly code: string;
  /** French label */
  readonly label: string;
  /** L2.5 semantic token — NOT a raw class (resolution happens here) */
  readonly token: StatusToken;
  readonly icon?: LucideIcon;
  readonly dotClass?: string;
}

/** Module status map: backend code → config (resolved by the wrapper, never by a `domain` prop). */
export type StatusConfigMap = Readonly<Record<string, StatusConfig>>;

export type StatusVariant = "solid" | "soft" | "muted";

export interface StatusBadgeProps {
  /** Raw backend status code */
  status: string;
  /** Resolved module status config (code → config) */
  config: StatusConfigMap;
  /** Optional label override (e.g. API-computed formatted status) */
  label?: string;
  /** Optional icon override — takes precedence over the config icon */
  icon?: LucideIcon;
  /** Visual variant: solid (default) = filled bg + saturated text,
   *  soft = tinted bg + saturated text, muted = light bg + attenuated text */
  variant?: StatusVariant;
  size?: "sm" | "md";
  className?: string;
}

/** Neutral fallback for unknown statuses — never crashes, low emphasis (--status-draft pair). */
const FALLBACK_MAP: Readonly<Record<string, StatusConfig>> = {
  INCONNU: { code: "INCONNU", label: "Inconnu", token: "draft" },
};

const SIZE_CLASSES = {
  sm: "px-2 py-0.5 text-[11px] gap-1.5",
  md: "px-2.5 py-1 text-xs gap-2",
} as const;

/** Base chrome — self-contained, no dependency on the legacy badge.css classes. */
const BASE_CLASSES = "inline-flex items-center rounded font-medium border whitespace-nowrap";

/**
 * Compute inline style for the given variant.
 * Uses the L2.5 token pair (--status-<token> fg, --status-<token>-bg bg)
 * and adjusts opacity/saturation per variant via color-mix.
 */
function variantStyle(token: string, variant: StatusVariant): CSSProperties {
  const fg = `var(--status-${token})`;
  const bg = `var(--status-${token}-bg)`;

  switch (variant) {
    case "soft":
      return {
        color: fg,
        backgroundColor: bg,
        borderColor: `color-mix(in oklab, ${fg} 20%, transparent)`,
      };
    case "muted":
      return {
        color: `color-mix(in oklab, ${fg} 70%, transparent)`,
        backgroundColor: `color-mix(in oklab, ${bg} 50%, transparent)`,
        borderColor: `color-mix(in oklab, ${fg} 10%, transparent)`,
      };
    default:
      return {
        color: fg,
        backgroundColor: bg,
        borderColor: `color-mix(in oklab, ${fg} 20%, transparent)`,
      };
  }
}

/**
 * Shared dumb status badge: resolves a backend code to its L2.5 token pair
 * (--status-<token> + --status-<token>-bg) via inline CSS vars — no domain
 * logic, no Tailwind arbitrary-value classes that could be purged.
 * Fully self-contained: fg/bg/border driven by the token pair only.
 */
export function StatusBadge({
  status,
  config,
  label,
  icon: iconOverride,
  variant = "solid",
  size = "sm",
  className,
}: StatusBadgeProps) {
  const resolved = config[status] ?? FALLBACK_MAP.INCONNU;
  const Icon = iconOverride ?? resolved.icon;
  const displayLabel = label ?? resolved.label;

  return (
    <span
      className={cn(BASE_CLASSES, SIZE_CLASSES[size], className)}
      style={variantStyle(resolved.token, variant)}
    >
      {Icon && <Icon className="w-3 h-3 shrink-0" />}
      {displayLabel}
    </span>
  );
}
